import * as React from 'react';
import { clsx } from 'clsx';
import { motion } from 'framer-motion';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'gradient';
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  children: React.ReactNode;
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  className,
  children,
  fullWidth = false,
  ...props
}) => {
  // Separate drag and animation related props to avoid conflicts
  const {
    onDrag, onDragEnd, onDragStart, onDragLeave, onDragEnter, onDragOver, onDrop,
    onAnimationStart, onAnimationEnd, onAnimationIteration,
    ...restProps
  } = props as any;
  const baseClasses = 'inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none';

  const variants: Record<ButtonVariant, string> = {
    primary: 'bg-primary hover:bg-opacity-90 text-primary-foreground focus:ring-primary shadow-md hover:shadow-lg hover:-translate-y-0.5',
    secondary: 'bg-secondary hover:bg-opacity-90 text-secondary-foreground focus:ring-secondary shadow-md hover:shadow-lg hover:-translate-y-0.5',
    outline: 'border-2 border-primary bg-transparent text-primary hover:bg-primary/10 focus:ring-primary/50 shadow-sm hover:shadow-md',
    ghost: 'text-text hover:bg-neutral-100 focus:ring-text/50',
    gradient: 'bg-gradient-to-r from-pink-500 to-orange-400 text-white focus:ring-pink-500/50 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transform transition-all duration-300 ease-in-out',
  };

  const sizes: Record<ButtonSize, string> = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-sm',
    lg: 'px-8 py-4 text-base',
    xl: 'px-10 py-5 text-lg',
  };

  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <motion.button
      whileHover={{ scale: disabled || loading ? 1 : 1.02 }}
      whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      className={clsx(
        baseClasses,
        variants[variant],
        sizes[size],
        widthClass,
        className
      )}
      disabled={disabled || loading}
      {...restProps}
    >
      {loading && (
        <motion.svg
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="-ml-1 mr-3 h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </motion.svg>
      )}
      {children}
    </motion.button>
  );
};